import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Eye, MessageSquare, ThumbsUp, ChevronLeft, Plus, Clock, Pin, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { formatError } from '../contexts/AuthContext';
import AttachmentPicker from '../components/AttachmentPicker';
import AdSlot from '../components/AdSlot';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import RichTextEditor from '../components/RichTextEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_STYLES = {
  l6e: { bg: 'bg-emerald-600', light: 'bg-emerald-50 text-emerald-700 border-emerald-200', text: 'text-emerald-700' },
  l7e: { bg: 'bg-cyan-600', light: 'bg-cyan-50 text-cyan-700 border-cyan-200', text: 'text-cyan-700' },
};

// Admin panelinden seçilen renk (cat.color) için stiller (başlık banner'ı burada daha koyu bir ton kullanır)
const COLOR_STYLES = {
  emerald: { bg: 'bg-emerald-600', light: 'bg-emerald-50 text-emerald-700 border-emerald-200', text: 'text-emerald-700' },
  sky: { bg: 'bg-sky-600', light: 'bg-sky-50 text-sky-700 border-sky-200', text: 'text-sky-700' },
  amber: { bg: 'bg-amber-500', light: 'bg-amber-50 text-amber-700 border-amber-200', text: 'text-amber-700' },
  rose: { bg: 'bg-rose-600', light: 'bg-rose-50 text-rose-700 border-rose-200', text: 'text-rose-700' },
  violet: { bg: 'bg-violet-600', light: 'bg-violet-50 text-violet-700 border-violet-200', text: 'text-violet-700' },
  slate: { bg: 'bg-slate-600', light: 'bg-slate-50 text-slate-700 border-slate-200', text: 'text-slate-700' },
  cyan: { bg: 'bg-cyan-600', light: 'bg-cyan-50 text-cyan-700 border-cyan-200', text: 'text-cyan-700' },
  orange: { bg: 'bg-orange-500', light: 'bg-orange-50 text-orange-700 border-orange-200', text: 'text-orange-700' },
  yellow: { bg: 'bg-yellow-500', light: 'bg-yellow-50 text-yellow-700 border-yellow-200', text: 'text-yellow-700' },
  blue: { bg: 'bg-blue-600', light: 'bg-blue-50 text-blue-700 border-blue-200', text: 'text-blue-700' },
  purple: { bg: 'bg-purple-600', light: 'bg-purple-50 text-purple-700 border-purple-200', text: 'text-purple-700' },
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ForumCategoryPage() {
  const { categorySlug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [topics, setTopics] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [sort, setSort] = useState('recent');
  const [loading, setLoading] = useState(true);
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newAttachments, setNewAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/forum/categories`).then(r => {
      const cat = r.data.categories?.find(c => c.slug === categorySlug);
      setCategory(cat || null);
    });
  }, [categorySlug]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/forum/topics?category=${categorySlug}&sort=${sort}&page=${page}&limit=20`)
      .then(r => {
        setTopics(r.data.topics || []);
        setTotal(r.data.total || 0);
        setPages(r.data.pages || 1);
      })
      .finally(() => setLoading(false));
  }, [categorySlug, sort, page]);

  const handleNewTopic = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) {
      toast.error('Başlık ve içerik zorunludur');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/forum/topics`, {
        category_slug: categorySlug,
        title: newTitle,
        content: newContent,
        attachments: newAttachments,
      }, { withCredentials: true });
      toast.success('Konu açıldı!');
      setShowNewTopic(false);
      setNewTitle('');
      setNewContent('');
      setNewAttachments([]);
      navigate(`/forum/konu/${r.data.topic.slug || r.data.topic._id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const cStyle = (category && COLOR_STYLES[category.color]) || CATEGORY_STYLES[categorySlug] || { bg: 'bg-slate-600', light: 'bg-slate-50 text-slate-700', text: 'text-slate-700' };

  // Prefer category.title_seo and category.description_seo if available, else sensible defaults
  const seoTitle = category ? `${category.name} · Forum` : 'Forum Kategorisi';
  const seoDesc = category?.description || `MiniOto forumunda ${category?.name || 'bu kategori'} ile ilgili son konular, kullanıcı deneyimleri ve ipuçları.`;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title={seoTitle}
        description={seoDesc}
      />
      <Breadcrumbs items={[
        { name: 'Forum', href: '/forum' },
        { name: category?.name || categorySlug },
      ]} />
      <AdSlot placement="forum_top" className="mb-6" />
      {/* Category header */}
      {category && (
        <div className={`rounded-2xl ${cStyle.bg} text-white p-6 mb-6`}>
          <h1 className="text-2xl font-extrabold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{category.name}</h1>
          <p className="text-white/80">{category.description}</p>
          <div className="mt-3 text-sm text-white/70">{total} konu</div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Sırala:</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-36 h-9" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">En Yeni</SelectItem>
              <SelectItem value="popular">En Beğenilen</SelectItem>
              <SelectItem value="replies">En Çok Yanıt</SelectItem>
              <SelectItem value="views">En Çok Görüntü</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {user && (
          <Dialog open={showNewTopic} onOpenChange={setShowNewTopic}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-topic-btn">
                <Plus className="w-4 h-4 mr-2" /> Yeni Konu
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Yeni Konu Aç</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleNewTopic} className="space-y-4 mt-2">
                <div>
                  <Label className="text-slate-700 font-medium">Başlık</Label>
                  <Input
                    value={newTitle}
                    onChange={e => setNewTitle(e.target.value)}
                    placeholder="Konu başlığını yazın..."
                    className="mt-1"
                    data-testid="new-topic-title"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">İçerik</Label>
                  <div className="mt-1" data-testid="new-topic-content">
                    <RichTextEditor
                      value={newContent}
                      onChange={setNewContent}
                      placeholder="Konunuzu detaylı yazın..."
                      minHeight={200}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Ekler (opsiyonel)</Label>
                  <div className="mt-1">
                    <AttachmentPicker attachments={newAttachments} setAttachments={setNewAttachments} testIdPrefix="cat-new-attach" />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowNewTopic(false)}>İptal</Button>
                  <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-topic-btn">
                    {submitting ? 'Gönderiliyor...' : 'Konuyu Yayınla'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Topics list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        </div>
      ) : topics.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
          <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">Bu kategoride henüz konu yok</p>
          {user && (
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setShowNewTopic(true)}>
              İlk Konuyu Aç
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          {topics.map((topic, i) => (
            <Link
              key={topic._id}
              to={`/forum/konu/${topic.slug || topic._id}`}
              data-testid={`topic-item-${topic._id}`}
              className={`flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors ${i > 0 ? 'border-t border-slate-50' : ''}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {topic.is_pinned && (
                    <span className="flex items-center gap-1 text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full border border-yellow-200">
                      <Pin className="w-3 h-3" /> Sabit
                    </span>
                  )}
                  {topic.is_locked && (
                    <span className="flex items-center gap-1 text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-200">
                      <Lock className="w-3 h-3" /> Kilitli
                    </span>
                  )}
                </div>
                <div className="font-semibold text-slate-800 truncate">{topic.title}</div>
                <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                  <span className="font-medium text-slate-500">{topic.author_display_name}</span>
                  <span>·</span>
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(topic.created_at)}</span>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-4 text-xs text-slate-400 shrink-0">
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{topic.views || 0}</span>
                <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{topic.reply_count || 0}</span>
                <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{topic.like_count || 0}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
            Önceki
          </Button>
          <span className="text-sm text-slate-500">{page} / {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
            Sonraki
          </Button>
        </div>
      )}
    </div>
  );
}
